import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpTrayIcon, ChatBubbleLeftIcon, ChevronLeftIcon, ChevronRightIcon, DocumentIcon, MagnifyingGlassIcon,
  PaperClipIcon, PlusIcon,
} from '@heroicons/react/24/outline';
import Modal from '../components/Modal';
import { PageHeader, Panel, States, selectClass } from '../components/OpsPageKit';
import {
  fetchFleetFile, useAddFleetNote, useFleetRecord, useFleetRegisterConfig, useFleetRegisterList, useSaveFleetRecord,
  useUploadFleetFile,
} from '../hooks/useFleetRegisters';

// Fleet registers: Driver Licence Checks, Fines & PCNs and Rental Van
// Inspections, rebuilt from monday.com, plus the read-only Fleet Master backup.
// One page for all of them — what each one shows (columns, filters, stages,
// form fields, file types, read-only) comes from the tech server's
// config/fleetRegisters.js.

const inputClass = 'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';
const ukDate = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('/') : '—');
const ukDateTime = (d) => (d ? new Date(d).toLocaleString('en-GB', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London',
}).replace(',', '') : '—');
const daysUntil = (iso) => (iso ? Math.round((new Date(`${String(iso).slice(0, 10)}T00:00:00`) - new Date(new Date().toDateString())) / 86400000) : null);

function show(field, v) {
  if (v === undefined || v === null || v === '') return '—';
  if (field.type === 'date') return ukDate(v);
  if (field.type === 'money') return `£${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return String(v);
}

// Licence expiry and the like: red once passed, amber inside the warning window.
function KeyDate({ cfg, iso }) {
  if (!iso) return <span className="text-gray-400">—</span>;
  const d = daysUntil(iso);
  const warn = cfg.keyDate?.warnDays;
  if (warn && d < 0) return <span className="font-semibold text-red-700">{ukDate(iso)} <span className="block text-[11px] font-normal">expired</span></span>;
  if (warn && d <= warn) return <span className="font-semibold text-amber-700">{ukDate(iso)} <span className="block text-[11px] font-normal">in {d} day{d === 1 ? '' : 's'}</span></span>;
  return <span>{ukDate(iso)}</span>;
}

// A stored image, fetched with the sign-in token (a plain <img src> cannot).
function AuthThumb({ register, recordId, file }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    let made = null;
    if (file.has_thumb) {
      fetchFleetFile(register, recordId, file.id, true).then((u) => { made = u; if (alive) setUrl(u); }).catch(() => {});
    }
    return () => { alive = false; if (made) URL.revokeObjectURL(made); };
  }, [register, recordId, file.id, file.has_thumb]);
  if (url) return <img src={url} alt={file.original_name} className="h-full w-full object-cover" />;
  return <DocumentIcon className="h-8 w-8 text-gray-300" />;
}

async function openFile(register, recordId, file) {
  // Opened before the fetch so the browser treats it as the user's own click.
  const win = window.open('', '_blank');
  try {
    const url = await fetchFleetFile(register, recordId, file.id, false);
    if (win) win.location.href = url; else window.location.href = url;
  } catch {
    if (win) win.close();
  }
}

// Photos are shrunk in the browser (longest side 2000px) before upload: a
// phone photo is 4–8 MB and the record only needs it legible.
function readForUpload(file) {
  return new Promise((resolve, reject) => {
    const asBase64 = (blob, name) => {
      const fr = new FileReader();
      fr.onload = () => resolve({ name, contentBase64: String(fr.result).split(',')[1], size: blob.size });
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    };
    if (!/^image\/(jpeg|png|webp)$/.test(file.type) || file.size < 1.5 * 1024 * 1024) return asBase64(file, file.name);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, 2000 / Math.max(img.width, img.height));
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      c.toBlob((b) => asBase64(b || file, file.name.replace(/\.(png|webp)$/i, '.jpg')), 'image/jpeg', 0.82);
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => asBase64(file, file.name);
    img.src = URL.createObjectURL(file);
  });
}

function FieldInput({ field, value, onChange }) {
  const v = value ?? '';
  if (field.readOnly) return <p className="py-2 text-sm text-gray-600">{show(field, value)}</p>;
  if (field.type === 'select') {
    return (
      <select value={v} onChange={(e) => onChange(e.target.value)} className={inputClass}>
        <option value="">—</option>
        {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
        {v && !field.options.includes(v) ? <option value={v}>{v}</option> : null}
      </select>
    );
  }
  if (field.type === 'long') return <textarea rows={3} value={v} onChange={(e) => onChange(e.target.value)} className={inputClass} />;
  const type = { date: 'date', number: 'number', money: 'number', email: 'email' }[field.type] || 'text';
  return <input type={type} step={field.type === 'money' ? '0.01' : undefined} value={v} onChange={(e) => onChange(e.target.value)} className={inputClass} />;
}

// Fields under their `section` headings, in config order; a register without
// sections is one untitled group.
function fieldGroups(fields) {
  const groups = [];
  for (const f of fields) {
    const name = f.section || '';
    let g = groups.find((x) => x.name === name);
    if (!g) { g = { name, fields: [] }; groups.push(g); }
    g.fields.push(f);
  }
  return groups;
}

function FilesSection({ cfg, register, recordId, files }) {
  const upload = useUploadFleetFile(register, recordId);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const byKind = useMemo(() => {
    const m = {};
    (files || []).forEach((f) => { (m[f.kind] = m[f.kind] || []).push(f); });
    return m;
  }, [files]);

  const add = async (kind, list) => {
    setError('');
    for (const file of Array.from(list || [])) {
      setBusy(`Uploading ${file.name}…`);
      try {
        const prepared = await readForUpload(file);
        if (prepared.size > 10 * 1024 * 1024) { setError(`${file.name} is over 10 MB.`); continue; }
        await upload.mutateAsync({ kind, name: prepared.name, contentBase64: prepared.contentBase64 });
      } catch (e) {
        setError(e?.response?.data?.error || `${file.name} did not upload.`);
      }
    }
    setBusy('');
  };

  return (
    <div className="space-y-4">
      {cfg.files.filter((k) => k.kind !== 'other' || byKind.other).map((k) => (
        <div key={k.kind}>
          <div className="mb-2 flex items-center justify-between gap-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {k.label} <span className="font-normal normal-case text-gray-400">({(byKind[k.kind] || []).length})</span>
            </h4>
            {cfg.readOnly ? null : (
              <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:border-brand">
                <ArrowUpTrayIcon className="h-3.5 w-3.5" /> Add
                <input type="file" multiple className="hidden" onChange={(e) => { add(k.kind, e.target.files); e.target.value = ''; }} />
              </label>
            )}
          </div>
          {(byKind[k.kind] || []).length ? (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
              {byKind[k.kind].map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => openFile(register, recordId, f)}
                  title={`${f.original_name} · ${f.uploaded_by_name || ''} ${ukDate(f.uploaded_at)}`}
                  className="group flex flex-col overflow-hidden rounded-md border border-gray-200 bg-white text-left hover:border-brand"
                >
                  <span className="flex h-24 items-center justify-center overflow-hidden bg-gray-50">
                    <AuthThumb register={register} recordId={recordId} file={f} />
                  </span>
                  <span className="truncate px-1.5 py-1 text-[11px] text-gray-600">{f.original_name}</span>
                </button>
              ))}
            </div>
          ) : <p className="text-xs text-gray-400">None.</p>}
        </div>
      ))}
      {busy ? <p className="text-xs text-gray-500">{busy}</p> : null}
      {error ? <p className="text-xs font-medium text-red-600">{error}</p> : null}
    </div>
  );
}

function NotesSection({ register, recordId, notes, readOnly }) {
  const add = useAddFleetNote(register, recordId);
  const [text, setText] = useState('');
  return (
    <div>
      <div className={readOnly ? 'hidden' : 'flex gap-2'}>
        <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} className={inputClass} placeholder="Add a note…" />
        <button
          type="button"
          disabled={!text.trim() || add.isPending}
          onClick={() => add.mutate(text, { onSuccess: () => setText('') })}
          className="self-start rounded-md bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50"
        >
          Add
        </button>
      </div>
      <ul className="mt-3 space-y-2">
        {(notes || []).map((n) => (
          <li key={n.id} className="rounded-md border border-gray-200 bg-white p-2.5 text-sm">
            <p className="whitespace-pre-wrap text-gray-800">{n.body}</p>
            <p className="mt-1 text-[11px] text-gray-400">{n.author_name || 'Unknown'} · {ukDateTime(n.created_at)}</p>
          </li>
        ))}
        {!(notes || []).length ? <li className="text-xs text-gray-400">No notes yet.</li> : null}
      </ul>
    </div>
  );
}

function RecordModal({ cfg, register, id: initialId, onClose }) {
  const [id, setId] = useState(initialId);
  const { data, isLoading } = useFleetRecord(register, id);
  const save = useSaveFleetRecord(register);
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');

  // Filled from the record once per record: a refetch after adding a photo or
  // a note must not throw away edits not yet saved.
  useEffect(() => {
    if (!id) setForm((f) => f || { title: '', stage: cfg.stages[0], data: {} });
    else if (data?.record) setForm((f) => (f && f.forId === id ? f : { forId: id, title: data.record.title, stage: data.record.stage, data: { ...data.record.data } }));
  }, [id, data, cfg.stages]);

  const set = (k, v) => setForm((f) => ({ ...f, data: { ...f.data, [k]: v } }));
  const submit = () => {
    setError(''); setSaved('');
    save.mutate({ id, title: form.title, stage: form.stage, data: form.data }, {
      onSuccess: (r) => {
        if (!id && r.id) { setId(r.id); setSaved('Created. You can add files and notes now.'); }
        else setSaved(r.changed ? 'Saved.' : 'Nothing had changed.');
      },
      onError: (e) => setError(e?.response?.data?.error || 'That did not save.'),
    });
  };

  const rec = data?.record;
  // A read-only register (the Fleet Master backup) shows every field as text:
  // no inputs, no Save, no uploads or notes.
  const ro = Boolean(cfg.readOnly);
  // The title field duplicates the registration on the backup, so it is not
  // shown twice.
  const fields = ro ? cfg.fields.filter((f) => !(f.key === 'registration' && cfg.titleLabel === 'Registration')) : cfg.fields;
  const groups = fieldGroups(fields);
  return (
    <Modal
      title={id ? (rec?.title || '…') : `New ${cfg.noun}`}
      subtitle={rec ? `${cfg.label} · ${rec.monday_item_id ? `from monday (${ukDate(rec.monday_created_at)})` : `added by ${rec.created_by_name || '—'}`} · last updated ${ukDateTime(rec.updated_at)}${rec.updated_by_name ? ` by ${rec.updated_by_name}` : ''}` : cfg.label}
      onClose={onClose}
      width="max-w-4xl"
      footer={(
        <div className="flex flex-wrap items-center justify-end gap-2">
          {error ? <span className="mr-auto text-sm font-medium text-red-600">{error}</span> : null}
          {saved ? <span className="mr-auto text-sm font-medium text-emerald-700">{saved}</span> : null}
          {ro ? <span className="mr-auto text-xs text-gray-500">Read-only backup: change it in the Fleet Master itself.</span> : null}
          <button type="button" onClick={onClose} className="rounded-md px-4 py-2 text-sm text-gray-600 hover:bg-gray-100">Close</button>
          {ro ? null : (
            <button type="button" onClick={submit} disabled={!form || save.isPending} className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50">
              {save.isPending ? 'Saving…' : id ? 'Save changes' : `Create ${cfg.noun}`}
            </button>
          )}
        </div>
      )}
    >
      {(id && isLoading) || !form ? <p className="text-sm text-gray-400">Loading…</p> : (
        <div className="space-y-5">
          <section className="grid gap-3 rounded-lg border border-gray-200 bg-white p-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
              {cfg.titleLabel}
              {ro
                ? <p className="py-2 text-sm text-gray-600">{form.title || '—'}</p>
                : <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputClass} />}
            </label>
            {cfg.stages.length > 1 ? (
              <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
                Stage
                {ro ? <p className="py-2 text-sm text-gray-600">{form.stage || '—'}</p> : (
                  <select value={form.stage || ''} onChange={(e) => setForm({ ...form, stage: e.target.value })} className={inputClass}>
                    {cfg.stages.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                )}
              </label>
            ) : null}
          </section>
          {groups.map((g) => (
            <section key={g.name || 'fields'} className="rounded-lg border border-gray-200 bg-white p-4">
              {g.name ? <h3 className="mb-3 text-sm font-bold text-gray-900">{g.name}</h3> : null}
              <div className="grid gap-3 sm:grid-cols-2">
                {g.fields.map((f) => (
                  <label key={f.key} className={`flex flex-col gap-1 text-xs font-medium text-gray-600 ${f.type === 'long' ? 'sm:col-span-2' : ''}`}>
                    <span>{f.label}{f.required && !ro ? ' *' : ''}{f.sensitive ? <span className="ml-1 font-normal text-gray-400">(personal: Fleet only)</span> : null}</span>
                    <FieldInput field={ro ? { ...f, readOnly: true } : f} value={form.data[f.key]} onChange={(v) => set(f.key, v)} />
                  </label>
                ))}
              </div>
            </section>
          ))}

          {id ? (
            <>
              <section className="rounded-lg border border-gray-200 bg-white p-4">
                <h3 className="mb-3 flex items-center gap-1.5 text-sm font-bold text-gray-900"><PaperClipIcon className="h-4 w-4" /> Files</h3>
                <FilesSection cfg={cfg} register={register} recordId={id} files={data?.files} />
              </section>
              <section className="rounded-lg border border-gray-200 bg-white p-4">
                <h3 className="mb-3 flex items-center gap-1.5 text-sm font-bold text-gray-900"><ChatBubbleLeftIcon className="h-4 w-4" /> Notes and history</h3>
                <NotesSection register={register} recordId={id} notes={data?.notes} readOnly={ro} />
              </section>
            </>
          ) : <p className="text-xs text-gray-500">Create the {cfg.noun} first, then add its files and notes.</p>}
        </div>
      )}
    </Modal>
  );
}

export default function FleetRegister({ register }) {
  const { data: configs, isLoading: cfgLoading, isError: cfgError } = useFleetRegisterConfig();
  const cfg = configs?.[register];
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState('');
  const [filters, setFilters] = useState({});
  const [due, setDue] = useState('');
  const [sort, setSort] = useState('date_desc');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(null); // record id, or 'new'

  useEffect(() => { const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 300); return () => clearTimeout(t); }, [searchInput]);
  useEffect(() => { setStage(''); setFilters({}); setDue(''); setPage(1); setSearchInput(''); }, [register]);

  const params = {
    search: search || undefined, stage: stage || undefined, due: due || undefined, sort, page, pageSize: 50,
    ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v).map(([k, v]) => [`f_${k}`, v])),
  };
  const list = useFleetRegisterList(register, params);
  const rows = list.data?.rows || [];
  const total = list.data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / 50));
  const stageCounts = list.data?.stageCounts || {};
  const allCount = Object.values(stageCounts).reduce((n, x) => n + x, 0);

  if (cfgLoading || cfgError || !cfg) return <div className="p-4 sm:p-6"><Link to="/?dept=fleet" className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark"><ChevronLeftIcon className="h-4 w-4" /> Back to Fleet</Link><States isLoading={cfgLoading} isError={cfgError || (!cfgLoading && !cfg)} what="this register" /></div>;
  const listFields = cfg.fields.filter((f) => f.list && f.key !== cfg.keyDate?.field);

  return (
    <div className="p-4 sm:p-6">
      <Link to="/?dept=fleet" className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark">
        <ChevronLeftIcon className="h-4 w-4" /> Back to Fleet
      </Link>
      <PageHeader title={cfg.label} subtitle={cfg.description} onRefresh={() => list.refetch()} fetching={list.isFetching}>
        {cfg.readOnly ? null : (
          <button type="button" onClick={() => setOpen('new')} className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-dark">
            <PlusIcon className="h-4 w-4" /> New {cfg.noun}
          </button>
        )}
      </PageHeader>

      {cfg.readOnly ? (
        <p className="mt-3 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800">
          Read-only backup, copied from monday.com {cfg.mirrorSchedule || 'regularly'}. Nothing here can be edited —
          the SharePoint Fleet Master is still the fleet master.
        </p>
      ) : null}

      {cfg.stages.length > 1 ? (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {[['', `All (${allCount})`], ...cfg.stages.map((s) => [s, `${s} (${stageCounts[s] || 0})`])].map(([value, label]) => (
            <button
              key={label}
              type="button"
              onClick={() => { setStage(value); setPage(1); }}
              className={`rounded-full border px-3 py-1 text-xs font-semibold ${stage === value ? 'border-brand bg-brand text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-brand'}`}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-2 h-4 w-4 text-gray-400" />
          <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search name, registration, email, reference…" className={`${selectClass} w-full pl-8`} />
        </div>
        {cfg.fields.filter((f) => f.filter).map((f) => (
          <select key={f.key} value={filters[f.key] || ''} onChange={(e) => { setFilters({ ...filters, [f.key]: e.target.value }); setPage(1); }} className={selectClass}>
            <option value="">{f.label}: all</option>
            {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
            <option value="__none__">{f.label}: not set</option>
          </select>
        ))}
        {cfg.keyDate?.warnDays ? (
          <select value={due} onChange={(e) => { setDue(e.target.value); setPage(1); }} className={selectClass}>
            <option value="">{cfg.keyDate.label}: any</option>
            <option value="expired">Expired</option>
            <option value="soon">Due in {cfg.keyDate.warnDays} days</option>
          </select>
        ) : null}
        <select value={sort} onChange={(e) => setSort(e.target.value)} className={selectClass}>
          <option value="date_desc">{cfg.keyDate?.label || 'Date'}: newest first</option>
          <option value="date_asc">{cfg.keyDate?.label || 'Date'}: oldest first</option>
          <option value="title">{cfg.titleLabel}: A–Z</option>
          <option value="updated">Recently updated</option>
        </select>
      </div>

      <div className="mt-4">
        <States isLoading={list.isLoading} isError={list.isError} what={cfg.label.toLowerCase()} />
        {list.data ? (
          <Panel
            title={`${total.toLocaleString('en-GB')} ${total === 1 ? cfg.noun : `${cfg.noun}s`}`}
            right={(
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded p-1 hover:bg-gray-100 disabled:opacity-30" aria-label="Previous page"><ChevronLeftIcon className="h-4 w-4" /></button>
                Page {page} of {pages}
                <button type="button" disabled={page >= pages} onClick={() => setPage(page + 1)} className="rounded p-1 hover:bg-gray-100 disabled:opacity-30" aria-label="Next page"><ChevronRightIcon className="h-4 w-4" /></button>
              </div>
            )}
          >
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-3 py-2">{cfg.titleLabel}</th>
                    {cfg.keyDate ? <th className="px-3 py-2">{cfg.keyDate.label}</th> : null}
                    {listFields.map((f) => <th key={f.key} className="px-3 py-2">{f.label}</th>)}
                    {cfg.stages.length > 1 ? <th className="px-3 py-2">Stage</th> : null}
                    <th className="px-3 py-2 text-right">Files · notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((r) => (
                    <tr key={r.id} onClick={() => setOpen(r.id)} className="cursor-pointer hover:bg-brand-light/40">
                      <td className="px-3 py-2 font-medium text-gray-900">
                        {r.title}
                        {r.entra_user_id ? null : r.person_email ? <span className="block text-[11px] font-normal text-gray-400">not a current employee</span> : null}
                      </td>
                      {cfg.keyDate ? <td className="whitespace-nowrap px-3 py-2"><KeyDate cfg={cfg} iso={r.key_date} /></td> : null}
                      {listFields.map((f) => <td key={f.key} className="px-3 py-2 text-gray-700">{show(f, r.data[f.key])}</td>)}
                      {cfg.stages.length > 1 ? <td className="px-3 py-2 text-xs text-gray-600">{r.stage}</td> : null}
                      <td className="whitespace-nowrap px-3 py-2 text-right text-xs text-gray-500">{r.file_count} · {r.note_count}</td>
                    </tr>
                  ))}
                  {!rows.length ? <tr><td colSpan={20} className="px-3 py-6 text-center text-sm text-gray-400">Nothing matches.</td></tr> : null}
                </tbody>
              </table>
            </div>
          </Panel>
        ) : null}
      </div>

      {open ? <RecordModal cfg={cfg} register={register} id={open === 'new' ? null : open} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}
